import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { theme } from 'styled-tools'
import { useLocation } from 'react-router-dom'
import { CUSTOMER_FORM_SUPPORTED_TYPES } from './constants'
import InformationInputIcon from '../icons/informationInput'
import CheckmarkFilled from '../icons/checkmarkFilled'
import CheckmarkSVG from '../checkbox/CheckmarkSVG'
import { FormSelectQuestion } from './questions/FormSelectQuestion'
import { FormSliderQuestion } from './questions/FormSliderQuestion'

const QuestionBlockItemCustomerForm = styled.div`
  margin-bottom: 20px;
`

const QuestionContainer = styled.div`
  margin-bottom: 12px;
`

const QuestionHeadingCustomerForm = styled.div`
  display: inline-block;
  color: ${theme('colors.darkGrey')};
  font-size: ${theme('forms.label.fontSize')} !important;
  font-weight: ${theme('forms.label.fontWeight')};
  line-height: ${theme('forms.label.lineHeight')};
  letter-spacing: normal;
  text-transform: none;
  padding: 0;
`

const QuestionDescription = styled.p`
  font-family: ${theme('typography.paragraph.fontFamily')};
  font-size: ${theme('quickQuestions.preamble.fontSize.mobile')};
  font-weight: ${(props) =>
    theme('quickQuestions.preamble.fontWeight')(props) || '300'};
  line-height: ${theme('quickQuestions.preamble.lineHeight')};
  margin: 0 0 8px;
  color: ${theme(`quickQuestions.preamble.color`)};
`

const ValidationIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
`

const HeadingWithCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${theme('forms.label.marginBottom')};
`

const NotSureCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 16px;
  white-space: nowrap;
`

const NotSureText = styled.span`
  color: ${theme('colors.darkSlate')};
  font-weight: 400;
  font-size: 14px;
  font-family: ${theme('typography.paragraph.fontFamily')};
  margin-left: 8px;
`

const StyledCheckbox = styled.span`
  display: inline-block;
  width: 19px;
  height: 19px;
  border: 1px solid #7f8487;
  border-radius: 3px;
  background-color: #fff;
  transition: background-color 0.1s ease-in;
  position: relative;
  flex-shrink: 0;
  ${(props) =>
    props.active &&
    `
    background-color: #000000;
  `}
  svg {
    position: absolute;
    top: 3px;
    left: 3px;
  }
`

const StyledErrorIcon = styled(InformationInputIcon)`
  color: ${theme('forms.colors.invalid')};
`

const StyledValidIcon = styled(CheckmarkFilled)`
  color: ${theme('forms.colors.valid')};
`

const FormQuestionBlock = ({ question, answers, onUpdate }) => {
  const location = useLocation()
  const questionType = useMemo(
    () => question.optionType || '',
    [question.optionType]
  )

  const showValidationError = useMemo(
    () => question.notAnswered === true,
    [question.notAnswered]
  )

  const showValidIcon = useMemo(
    () => question.notAnswered === false,
    [question.notAnswered]
  )

  const isOtherChecked = useMemo(
    () => answers?.includes(question.otherAnswerText) || false,
    [answers, question]
  )

  const toggleNotSure = useCallback(() => {
    if (isOtherChecked) {
      onUpdate([])
    } else {
      onUpdate([question.otherAnswerText])
    }
  }, [question.otherAnswerText, isOtherChecked, onUpdate])

  const update = useCallback(
    (value) => {
      onUpdate(value)
    },
    [onUpdate]
  )

  const formattedId = useMemo(
    () =>
      `block_${(question.questionText || 'default')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/gi, '')
        .replace(/\s+/g, '_')}`,
    [question.questionText]
  )

  const focused = useMemo(() => {
    const focusValue = new URLSearchParams(location.search).get('block-focus')
    if (!focusValue) {
      return true
    }

    return focusValue === formattedId
  }, [location, formattedId])

  if (!questionType || !focused) {
    return null
  }

  const displayQuestionText = useMemo(
    () => question.questionTextOnBadaForm || question.questionText,
    [question.questionTextOnBadaForm, question.questionText]
  )

  if (!CUSTOMER_FORM_SUPPORTED_TYPES.includes(questionType)) {
    return null
  }

  return (
    <QuestionBlockItemCustomerForm
      data-track={`section-${question.sortOrder}`}
      data-cy="question-block"
      requiresAnswer={showValidationError}
      id={formattedId}
    >
      <QuestionContainer>
        {displayQuestionText &&
          questionType === 'slider' &&
          question.otherAnswerText && (
            <HeadingWithCheckboxWrapper>
              <ValidationIconWrapper style={{ marginBottom: 0, flex: 1 }}>
                <QuestionHeadingCustomerForm
                  dangerouslySetInnerHTML={{ __html: displayQuestionText }}
                />
                {showValidIcon && <StyledValidIcon width={23} height={23} />}
                {showValidationError && (
                  <StyledErrorIcon width={23} height={23} />
                )}
              </ValidationIconWrapper>
              <NotSureCheckboxWrapper onClick={toggleNotSure}>
                <StyledCheckbox active={isOtherChecked}>
                  <CheckmarkSVG />
                </StyledCheckbox>
                <NotSureText>{question.otherAnswerText}</NotSureText>
              </NotSureCheckboxWrapper>
            </HeadingWithCheckboxWrapper>
          )}
        {displayQuestionText &&
          (questionType !== 'slider' || !question.otherAnswerText) && (
            <ValidationIconWrapper>
              <QuestionHeadingCustomerForm
                dangerouslySetInnerHTML={{ __html: displayQuestionText }}
              />
              {showValidIcon && <StyledValidIcon width={23} height={23} />}
              {showValidationError && (
                <StyledErrorIcon width={23} height={23} />
              )}
            </ValidationIconWrapper>
          )}
        {!!question.questionDescription && (
          <QuestionDescription>
            {question.questionDescription}
          </QuestionDescription>
        )}
      </QuestionContainer>
      {questionType === 'single' && (
        <FormSelectQuestion
          question={question}
          answers={answers}
          onChange={update}
        />
      )}
      {questionType === 'slider' && (
        <FormSliderQuestion
          question={question}
          answers={answers}
          onChange={update}
        />
      )}
    </QuestionBlockItemCustomerForm>
  )
}

export default FormQuestionBlock
